/**
 * Users list page. SASADM-0106 (saas-auth SASATH-0212): server-paged and
 * server-searched. Users is the largest table on the platform, so nothing here
 * full-fetches it — the loader pulls only the first page and every page/size/
 * search change is a fresh `GET /users?page&size&q` round-trip. The table
 * itself (columns, rows, row-click navigation) is unchanged from spec 0021.
 */
import {
  Avatar,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type { PagedResponse, UserSummaryDto } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { useCopy } from '@/hooks/useCopy';

/** Rows per page the loader asks for, and the initial pager selection. */
const DEFAULT_SIZE = 50;
/** Search-box idle time before the query goes to the server. */
const SEARCH_DEBOUNCE_MS = 300;

export interface UsersListLoaderData {
  first: PagedResponse<UserSummaryDto>;
}

export async function usersListLoader(): Promise<UsersListLoaderData> {
  const first = await apiAdmin.users.listPaged({ page: 0, size: DEFAULT_SIZE });
  return { first };
}

export function UsersList() {
  const { first } = useLoaderData() as UsersListLoaderData;
  const { t } = useCopy();
  const navigate = useNavigate();
  const snack = useSnackbar();

  const [search, setSearch] = useState('');
  // The debounced search box — this is what the server actually sees.
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_SIZE);
  const [fetched, setFetched] = useState<PagedResponse<UserSummaryDto> | null>(null);

  // The loader already fetched exactly the unfiltered first page, so those
  // params are served from its data rather than re-queried; every other
  // combination is a round-trip. While one is in flight the previously loaded
  // page stays on screen instead of the table blanking out.
  const isLoaderParams = page === 0 && size === DEFAULT_SIZE && q === '';
  const result = isLoaderParams ? first : (fetched ?? first);

  // Sync the search box into `q` after the user stops typing. Leading/trailing
  // whitespace is dropped, but nothing else is: `%`, `_` and `\` are matched
  // literally by the server, so escaping them would corrupt the search.
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Fetch the page for the current params. The cleanup flag is the out-of-order
  // guard — a response whose params are no longer current is dropped rather
  // than rendered.
  useEffect(() => {
    if (isLoaderParams) return;
    let current = true;
    void (async () => {
      try {
        const res = await apiAdmin.users.listPaged({ page, size, q });
        if (current) setFetched(res);
      } catch (err) {
        if (current && err instanceof ApiError) {
          snack.error(t(`errors.codes.${err.code}`, err.code));
        }
      }
    })();
    return () => {
      current = false;
    };
    // `snack`/`t` are stable for the life of the page and are not params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, q, isLoaderParams]);

  // A new search term starts at the first page; so does a page-size change.
  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(0);
  }

  function handleSizeChange(value: number) {
    setSize(value);
    setPage(0);
  }

  return (
    <Box>
      <Breadcrumbs
        items={[{ label: t('nav.home', 'Home'), to: '/' }, { label: t('nav.users', 'Users') }]}
      />
      <PageHeader title={t('users.list.title', 'Users')} subtitle={t('users.list.subtitle', '')} />
      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Toolbar>
          <TextField
            size="small"
            placeholder={t('common.search', 'Search')}
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            sx={{ minWidth: 280 }}
            slotProps={{ htmlInput: { 'aria-label': t('common.search', 'Search') } }}
          />
        </Toolbar>
      </Paper>
      {result.totalElements === 0 ? (
        <Typography
          sx={{
            color: 'text.secondary',
            textAlign: 'center',
            py: 4,
          }}
        >
          {q
            ? t('users.list.noMatch', 'No users match your search.')
            : t('users.list.empty', 'No users found.')}
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>{t('users.list.columns.name', 'Name')}</TableCell>
                <TableCell>{t('users.list.columns.email', 'Email')}</TableCell>
                <TableCell>{t('users.list.columns.verified', 'Verified')}</TableCell>
                <TableCell>{t('users.list.columns.googleLinked', 'Google')}</TableCell>
                <TableCell>{t('users.list.columns.createdAt', 'Joined')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.content.map((u) => (
                <TableRow
                  key={u.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/users/${u.id}`)}
                >
                  <TableCell>
                    <Avatar
                      {...(u.imageUrl ? { src: u.imageUrl } : {})}
                      sx={{ width: 28, height: 28 }}
                    >
                      {(u.name ?? u.email)[0]?.toUpperCase()}
                    </Avatar>
                  </TableCell>
                  <TableCell>{u.name ?? '—'}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.emailVerified ? '✓' : '—'}</TableCell>
                  <TableCell>{u.googleLinked ? 'G' : '—'}</TableCell>
                  <TableCell>{new Date(u.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={result.totalElements}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => handleSizeChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </TableContainer>
      )}
    </Box>
  );
}
